import GA4React from "ga-4-react";
import { format } from "date-fns";
import { StorageServiceInstance } from "../services/storage";
import {
  numericDateFnsFormat,
  normalizeTeamRegion,
} from "./teamRegion";
import { Routes } from "./routes";
import {
  trackFanButtonClick,
  trackFanFunnelPageView,
  trackFanFunnelStep,
} from "./fanFunnelTracking";
import { analyticsTrackingAllowed } from "./analyticsConsent";

const GA_MEASUREMENT_ID = "G-CNJ22J348V";

let gaInitPromise: Promise<GA4React | null> | null = null;

function ensureGa(): Promise<GA4React | null> {
  if (!gaInitPromise) {
    gaInitPromise = (async () => {
      try {
        const ga = new GA4React(GA_MEASUREMENT_ID);
        await ga.initialize();
        return ga;
      } catch (err) {
        console.warn("clientAnalytics: GA4 init failed", err);
        return null;
      }
    })();
  }
  return gaInitPromise;
}

function buildGameParams(): Record<string, string> {
  const game = StorageServiceInstance.selectedGameData;
  if (!game?._id) return {};
  const start = game.startDate;
  const dateSuffix = typeof start === "number"
    ? format(
      start,
      numericDateFnsFormat(normalizeTeamRegion(game.team1?.teamRegion)),
    )
    : "";
  const stadiumLabel = typeof game.stadium === "object" && game.stadium && "name" in game.stadium
    ? String((game.stadium as { name?: string }).name || "")
    : String(game.stadium || "");
  return {
    game_id: String(game._id),
    game_name: `${game.team1?.name || "Team1"} vs ${game.team2?.name || "Team2"}`,
    game_context: `${game.team1?.name || ""} vs ${game.team2?.name || ""} @ ${
      stadiumLabel
    }, ${String(game.city || "")}, ${dateSuffix}`,
  };
}

type EngagementParam = string | number | boolean | undefined;

/** Maps router path to a stable funnel step id for GA4 / GTM */
const PATH_TO_PAGE_KEY: Record<string, string> = {
  [Routes.WELCOME]: "welcome",
  [Routes.TEAMS]: "teams",
  [Routes.GAMES]: "games",
  [Routes.SEAT]: "seat",
  [Routes.PLACE]: "place",
  [Routes.ENHANCED_GA_WHERE]: "ga_where",
  [Routes.ENHANCED_GA_AREA]: "ga_area",
  [Routes.ENHANCED_GA_MAP]: "ga_map",
  [Routes.DETAILS]: "details",
  [Routes.MY_MOMENTS]: "my_moments",
  [Routes.MOMENT]: "moment",
  [Routes.MOMENTO]: "momento",
  [Routes.ALL_MOMENTO]: "all_momento",
  [Routes.EDITOR]: "editor",
  [Routes.DOWNLOAD_OPTIONS]: "download_options",
  [Routes.IMAGE]: "image",
  [Routes.SIGNUP]: "signup",
  [Routes.MOREINFO]: "moreinfo",
  [Routes.MOMENTO_OPTIONS]: "momento_options",
  [Routes.PROFILE]: "profile",
  [Routes.PROFILE_GALLERY]: "profile_gallery",
  [Routes.VERIFY_SEAT]: "verify_seat",
  [Routes.OPT_OUT]: "opt_out",
};

export function getFanPageKeyFromPath(pathname: string): string {
  return PATH_TO_PAGE_KEY[pathname]
    || pathname.replace(/^\//, "").replace(/\//g, "_") || "unknown";
}

/** GA4 / dataLayer event name for a route, e.g. games_pageview, welcome_pageview */
export function getFanPageViewEventName(pathname: string): string {
  return `${getFanPageKeyFromPath(pathname)}_pageview`;
}

export type FanSocialShareChannel =
  | "facebook"
  | "twitter"
  | "instagram"
  | "snapchat"
  | "sms"
  | "email"
  | "native";

/**
 * Call on each SPA navigation. Sends gtag config + a route-specific pageview event
 * (`welcome_pageview`, `games_pageview`, …) and pushes the same to dataLayer.
 */
export async function trackFanPageView(pathname: string, pageTitle?: string): Promise<void> {
  try {
    trackFanFunnelPageView(pathname);
  } catch {
    // ignore
  }

  const allowed = await analyticsTrackingAllowed();
  if (!allowed) return;

  const page_key = getFanPageKeyFromPath(pathname);
  const pageViewEvent = `${page_key}_pageview`;
  const title = pageTitle ?? (typeof document !== "undefined" ? document.title : "");
  const gameParams = buildGameParams();

  try {
    const ga = await ensureGa();
    if (ga) {
      ga.gtag("config", GA_MEASUREMENT_ID, {
        page_path: pathname,
        page_title: title,
        page_key,
        ...gameParams,
      });
      ga.gtag("event", pageViewEvent, {
        page_path: pathname,
        page_title: title,
        page_key,
        event_category: "fan_pageview",
        ...gameParams,
      });
    }
  } catch (err) {
    console.warn("trackFanPageView", err);
  }

  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: pageViewEvent,
      page_path: pathname,
      page_title: title,
      page_key,
      ...gameParams,
    });
  }
}

/**
 * Custom interaction events (crop, scoreboard, purchase, etc.).
 * `action` should stay stable (e.g. fan_crop, fan_purchase) for GA4 explorations.
 */
export function trackFanEngagement(
  action: string,
  extra?: Record<string, EngagementParam>,
): void {
  const gameParams = buildGameParams();
  const payload: Record<string, EngagementParam> = {
    event_category: "fan_engagement",
    ...gameParams,
    ...extra,
  };

  if (action === "fan_purchase") {
    try {
      trackFanFunnelStep("purchase");
    } catch {
      // ignore
    }
  }
  if (action === "fan_view_all") {
    try {
      trackFanButtonClick("btn_view_all");
    } catch {
      // ignore
    }
  }
  if (action === "fan_crop") {
    try {
      trackFanButtonClick("btn_edit_crop");
    } catch {
      // ignore
    }
  }

  analyticsTrackingAllowed().then((allowed) => {
    if (!allowed) return;
    ensureGa()
      .then((ga) => {
        try {
          ga?.gtag("event", action, payload as Record<string, unknown>);
        } catch {
          /* ignore */
        }
      })
      .catch(() => undefined);

    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: action,
        ...payload,
      });
    }
  }).catch(() => undefined);
}

/** Social share actions: one event per tap, e.g. facebook_share, twitter_share */
export function trackFanSocialShare(
  channel: FanSocialShareChannel,
  extra?: Record<string, EngagementParam>,
): void {
  trackFanEngagement(`${channel}_share`, {
    event_category: "fan_share",
    ...extra,
  });
}

/** One GA4/dataLayer event per file the user saves (count multiple downloads separately). */
export function trackFanFileDownload(extra?: Record<string, EngagementParam>): void {
  trackFanEngagement("fan_download", extra);
  try {
    trackFanFunnelStep("download");
  } catch {
    // ignore
  }
}
